import {
  CONCERN_OPTIONS,
  EXAM_TIMEFRAME_OPTIONS,
  TRACK_OPTIONS,
  professionForTrack,
  trackOffersScenarios,
  type Concern,
  type ExamTimeframe,
  type Profession,
  type UserTrack,
} from "./constants";

/** The guided onboarding's profession- and track-varying COPY, as DATA
 *  (a track's own name and one-line description live with the track list,
 *  TRACK_OPTIONS in constants.ts). Step components read from here and never branch on profession
 *  themselves: tutorial mode's EMS-only coach showed what a hard-typed
 *  component does to the other professions (wrong-domain copy). A new
 *  profession is a new entry in `BY_PROFESSION`, and the coverage test makes
 *  a missing one fail.
 *
 *  Claims stay within what the product actually does for that profession.
 *  "Official skill sheet" is EMS-only (the NREMT psychomotor sheets); nursing
 *  and respiratory scenarios are graded step by step without that claim. */

type PlanPhase = { title: string; detail: string };

type ProfessionCopy = {
  /** Reframe screen body, after "That's not a knowledge gap. It's a reps gap." */
  reframeBody: string;
  /** How-it-works: three steps and the sample grade-sheet rows. */
  howItWorks: {
    title: string;
    steps: ReadonlyArray<{ title: string; detail: string }>;
    /** Heading over the sample lines; defaults to "Sample grade sheet". */
    sampleHeading?: string;
    sampleLines: ReadonlyArray<{ label: string; passed: boolean }>;
  };
  /** The four plan phases. */
  plan: ReadonlyArray<PlanPhase>;
};

const BY_PROFESSION: Record<Profession, ProfessionCopy> = {
  ems: {
    reframeBody:
      "You can't rehearse a skills station with flashcards. You need someone to play the patient, and someone to grade you the way the examiner will.",
    howItWorks: {
      title: "Run the call. Get graded like test day.",
      steps: [
        { title: "Pick a scenario", detail: "Medical and trauma calls built on the official skill sheets." },
        { title: "Talk to the AI patient", detail: "Type or speak. Your preceptor gives the cues an examiner would." },
        { title: "Get your score", detail: "Every line of the skill sheet, plus any critical fails." },
      ],
      sampleLines: [
        { label: "Scene size-up", passed: true },
        { label: "Primary survey", passed: true },
        { label: "History taking", passed: true },
        { label: "Reassessment", passed: false },
      ],
    },
    plan: [
      { title: "Medical assessment", detail: "Chest pain, shortness of breath, altered mental status" },
      { title: "Trauma assessment", detail: "Falls, crashes, penetrating injuries" },
      { title: "Harder calls", detail: "Hard medical and trauma scenarios" },
      { title: "Written exam prep", detail: "Question sets by exam domain" },
    ],
  },
  nursing: {
    reframeBody:
      "Reading about a deteriorating patient isn't the same as deciding what to do first while one is in front of you. You need a patient to practice on, and feedback on every decision.",
    howItWorks: {
      title: "Care for the patient. See every decision graded.",
      steps: [
        { title: "Pick a patient case", detail: "Realistic cases at your level of practice." },
        { title: "Assess and act", detail: "Type or speak. The patient responds to what you do." },
        { title: "Get your feedback", detail: "Each step graded, plus anything unsafe you missed." },
      ],
      sampleLines: [
        { label: "Recognize Cues", passed: true },
        { label: "Prioritize Hypotheses", passed: true },
        { label: "Take Action", passed: true },
        { label: "Evaluate Outcomes", passed: false },
      ],
    },
    plan: [
      { title: "Core patient cases", detail: "Assessment and what to do first" },
      { title: "Questions by subject", detail: "The areas your exam weighs most" },
      { title: "Harder cases", detail: "Patients who change on you" },
      { title: "Exam-style sets", detail: "Mixed questions, like the real exam" },
    ],
  },
  respiratory: {
    reframeBody:
      "The clinical simulations reward gathering the right information and making the right call in order. That's a skill you build by doing it, not by rereading.",
    howItWorks: {
      title: "Work the case. See every decision graded.",
      steps: [
        { title: "Pick a simulation", detail: "Patient cases built around the clinical simulation exam." },
        { title: "Gather and decide", detail: "Type or speak. The case responds to what you do." },
        { title: "Get your feedback", detail: "Each decision graded, plus anything harmful." },
      ],
      sampleLines: [
        { label: "Information gathering", passed: true },
        { label: "Assessment", passed: true },
        { label: "Decision making", passed: false },
        { label: "Follow-up", passed: true },
      ],
    },
    plan: [
      { title: "Clinical simulations", detail: "Information gathering and decisions" },
      { title: "TMC questions", detail: "Topic by topic" },
      { title: "Harder simulations", detail: "Cases that change as you go" },
      { title: "Exam-style sets", detail: "Mixed questions, like the real exam" },
    ],
  },
};

/** Tracks whose product differs from their profession's default. Today only
 *  the written-exam-only CRT track: no scenarios, so no scenario language. */
const TRACK_OVERRIDES: Partial<Record<UserTrack, Partial<ProfessionCopy>>> = {
  CRT: {
    reframeBody:
      "The TMC exam covers a lot of ground, and rereading notes doesn't tell you what you'd miss. You need questions that find the gaps, and explanations that close them.",
    howItWorks: {
      title: "Practice the questions. Close the gaps.",
      steps: [
        { title: "Pick a topic", detail: "Questions built around the TMC exam." },
        { title: "Answer and check", detail: "Instant feedback on every question." },
        { title: "See your progress", detail: "Where you're strong and where you're not." },
      ],
      sampleHeading: "Sample results by topic",
      sampleLines: [
        { label: "Patient data evaluation", passed: true },
        { label: "Equipment & infection control", passed: true },
        { label: "Interventions", passed: false },
      ],
    },
    plan: [
      { title: "TMC fundamentals", detail: "The core topics, one at a time" },
      { title: "Topic by topic", detail: "Questions by exam section" },
      { title: "Mixed practice", detail: "Everything, shuffled" },
      { title: "Exam-style sets", detail: "Mixed questions, like the real exam" },
    ],
  },
};

export function copyFor(track: UserTrack): ProfessionCopy {
  return { ...BY_PROFESSION[professionForTrack(track)], ...TRACK_OVERRIDES[track] };
}

export function trackLabel(track: UserTrack): string {
  return TRACK_OPTIONS.find((o) => o.value === track)?.label ?? track;
}

export function examTimeframeLabel(t: ExamTimeframe): string {
  return EXAM_TIMEFRAME_OPTIONS.find((o) => o.value === t)?.label ?? t;
}

/** Plan headline keyed on the exam timeframe. No invented dates: the user
 *  told us a range, so the copy speaks to the range. */
const PLAN_HEADLINE: Record<ExamTimeframe, string> = {
  within_2_weeks: "Two weeks. Let's make them count.",
  within_month: "A month out. That's enough time.",
  one_to_three_months: "You have time to do this right.",
  not_scheduled: "Build the habit before you book it.",
};
export function planHeadline(t: ExamTimeframe | undefined, track: UserTrack): string {
  return t ? PLAN_HEADLINE[t] : `Your ${trackLabel(track)} plan.`;
}

/** Rough weeks until the exam, for the pace screen's arithmetic. Null when
 *  there's no date to count toward. */
const WEEKS_UNTIL: Record<ExamTimeframe, number | null> = {
  within_2_weeks: 2,
  within_month: 4,
  one_to_three_months: 8,
  not_scheduled: null,
};

/** The pace screen's live readback. Pure arithmetic over what the user told
 *  us, labelled an estimate in the UI. */
export function paceReadback(goal: number, t: ExamTimeframe | undefined): string {
  const weeks = t ? WEEKS_UNTIL[t] : null;
  return weeks === null
    ? `That's about ${goal * 4} scenarios a month.`
    : `That's about ${goal * weeks} scenarios before your exam.`;
}

/** Paywall headline from the user's first-picked worry. */
const PAYWALL_HEADLINE: Record<Concern, string> = {
  critical_step: "Stop losing points on critical steps.",
  talking_it_out: "Get comfortable talking it out before test day.",
  prioritization: "Know what to do first, every time.",
  time: "Practice on the clock until it feels easy.",
  treatment_choice: "Know the right treatment, and when.",
  written_exam: "Walk into the written exam ready.",
};
export function paywallHeadline(concerns: ReadonlyArray<Concern> | undefined): string {
  const first = concerns?.[0];
  return first ? PAYWALL_HEADLINE[first] : "Get more practice before test day.";
}

/** One-line focus summary for the plan + loader ("Focus: critical steps and
 *  timing"). Lower-cased option labels joined in plain English. */
/** Lower-case only the first letter, so a label that contains "I" stays
 *  English mid-sentence. */
function lowerFirst(label: string): string {
  return label.charAt(0).toLowerCase() + label.slice(1);
}

export function concernsSummary(concerns: ReadonlyArray<Concern> | undefined): string | null {
  if (!concerns?.length) return null;
  const labels = concerns.map(
    (c) => lowerFirst(CONCERN_OPTIONS.find((o) => o.value === c)!.label),
  );
  return labels.length === 1
    ? labels[0]!
    : `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

/** The loader's timeline line. States the timeline the user gave; it does
 *  not claim the product paces anything (the plan is a presentation). A
 *  record, not string assembly, so "not scheduled" reads as English. */
const PACING_LINE: Record<ExamTimeframe, string> = {
  within_2_weeks: "Exam in the next 2 weeks",
  within_month: "Exam within a month",
  one_to_three_months: "Exam 1 to 3 months out",
  not_scheduled: "Exam not booked yet",
};
export function pacingLine(t: ExamTimeframe | undefined): string {
  return t ? PACING_LINE[t] : "Whenever your exam is";
}

/** The post-submit screens' wording. Keyed on whether the track has
 *  scenarios: a written-exam-only track (CRT) must never be promised a
 *  scenario or sent to the (empty, for it) scenario catalog. */
export function finishCopyFor(track: UserTrack | undefined) {
  const scenarios = !track || trackOffersScenarios(track);
  return scenarios
    ? {
        paywallBody: "Pick a plan, or start with a free scenario and upgrade anytime.",
        freePath: "Start with a free scenario",
        instructorTitle: "Run a scenario the way your students will.",
        instructorTry: {
          title: "Try a scenario",
          detail: "See the AI patient and the grade sheet firsthand.",
          href: "/scenarios",
        },
      }
    : {
        paywallBody: "Pick a plan, or start with free practice questions and upgrade anytime.",
        freePath: "Start with free practice",
        instructorTitle: "Practice the way your students will.",
        instructorTry: {
          title: "Try a practice set",
          detail: "See the questions and explanations your students get.",
          href: "/questions",
        },
      };
}
